import { getActiveSessionManager as SessionManager, createSessionWithSignature, genAuthNonce, genSolanaMessage, } from '@bounded-sh/core';
import { Buffer } from 'buffer';
import bs58 from 'bs58';
import { setCurrentUser, setAuthLoading } from '../../global';
import { getPlatform } from '../../platform';
import { setStoredAuthMethod } from '../stored-auth-method';
import { normalizeSolanaRpcUrl, resolveSolanaRpcUrl } from '../solana-rpc';
import { confirmAndCheckTransaction, transactionHasSignature } from './transaction-utils';
import { describeInstructionDivergence, transactionMessageBytes, transactionSignedBy, transactionV1SignedBy } from './transaction-identity';
import { defaultInjectedProvider, } from './injected-wallet-types';
import { firstStandardWalletProvider } from './wallet-standard-discovery';
// Re-export the type surface + discovery helper for callers who import the
// provider module directly.
export { defaultInjectedProvider };
const INJECTED_AUTH_METHOD = 'phantom';
const NO_WALLET_ERR = 'No Solana wallet found. Install a wallet like Phantom (phantom.app) and try again, or pass ' +
    'config.injectedWalletConfig.getProvider to point at your wallet. On a phone, await ' +
    'ensureWalletLoginReady() before offering wallet login - the mobile wallet only appears once ' +
    'it is registered, and preparing it after the tap loses the activation it needs.';
export class InjectedWalletProvider {
    constructor(config = {}, networkUrl = null, configuredNetwork = null) {
        var _a;
        // DS2-0369: the wallet address this session was minted for. The browser wallet
        // can silently switch accounts under us; if the currently selected account is
        // not the one we authenticated, we must REFUSE to sign rather than sign as a
        // principal the session never proved. A fresh login rebinds it.
        this.authenticatedAddress = null;
        if (typeof window === 'undefined') {
            throw new Error('InjectedWalletProvider can only be instantiated in a browser environment');
        }
        // `config.rpcUrl` is the legacy wallet-login-local override. Normalize
        // it before precedence so an empty legacy value cannot shadow the
        // current top-level config.rpcUrl. Re-initialization must also replace
        // every singleton field; otherwise a previous app's provider/RPC can
        // survive getAuthProvider()'s config-key rotation.
        const resolvedNetworkUrl = (_a = normalizeSolanaRpcUrl(config.rpcUrl)) !== null && _a !== void 0 ? _a : normalizeSolanaRpcUrl(networkUrl);
        const localNetwork = typeof config.network === 'string' && config.network.trim() !== ''
            ? config.network.trim()
            : null;
        const resolvedConfiguredNetwork = localNetwork !== null && localNetwork !== void 0 ? localNetwork : (typeof configuredNetwork === 'string' && configuredNetwork.trim() !== ''
            ? configuredNetwork.trim()
            : null);
        // Deliberately NOT a singleton. auth/index.ts already caches the active
        // provider by config key, so a second instance here bought nothing - and
        // it cost correctness: two overlapping logins shared one object, so each
        // overwrote the other's config. With a per-attempt gesture hook in that
        // config, one attempt could consume the OTHER's user tap and then wipe
        // its configuration on cleanup, handing the loser the winner's session.
        // Each construction now owns its state, and only the attempt that
        // succeeds is committed as the active provider.
        this.config = Object.assign({}, config);
        this.networkUrl = resolvedNetworkUrl;
        this.configuredNetwork = resolvedConfiguredNetwork;
    }
    /**
     * Replace the wallet config without disturbing the account binding. Used to retire a ONE-SHOT gesture hook once the login
     * that supplied it is over: that closure owns a specific piece of UI, so a
     * later operation must never be left waiting on it.
     */
    setWalletConfig(config) {
        this.config = Object.assign({}, config);
    }
    resolveProvider() {
        var _a, _b;
        const provider = (_b = (_a = (this.config.getProvider ? this.config.getProvider() : null)) !== null && _a !== void 0 ? _a : defaultInjectedProvider()) !== null && _b !== void 0 ? _b : firstStandardWalletProvider(this.configuredNetwork);
        if (!provider)
            throw new Error(NO_WALLET_ERR);
        return provider;
    }
    /**
     * Connect for an operation on an EXISTING session. A restored session holds
     * an address but no wallet connection, so every signing path starts by
     * reconnecting - silently when the wallet still trusts us.
     *
     * When the silent connect finds nothing (an MWA authorization that expired,
     * was cleared, or belongs to another chain), reconnecting means an
     * interactive round trip out to the wallet app - the same intent navigation
     * as the signature that follows, and it needs its own transient activation.
     * Taking a gesture for it is what makes a restored mobile session usable:
     * without one the reconnect is dispatched from whatever activation is left
     * after the caller's own awaits (a governed write has already been to the
     * server by then), Chrome blocks it, and the protocol reports the wallet as
     * missing rather than as needing authorization.
     */
    async connectForAction(provider) {
        try {
            return await this.connectAddress(provider, true);
        }
        catch (_a) {
            await this.requireFreshTap(provider, 'connect');
            return this.connectAddress(provider);
        }
    }
    /** Connect the injected wallet (if needed) and return its Solana address. */
    async connectAddress(provider, onlyIfTrusted = false) {
        var _a, _b;
        // Already connected — reuse the pubkey.
        if (provider.publicKey && ((_a = provider.isConnected) !== null && _a !== void 0 ? _a : true)) {
            return provider.publicKey.toString();
        }
        const res = await provider.connect(onlyIfTrusted ? { onlyIfTrusted: true } : undefined);
        const pk = (_b = res === null || res === void 0 ? void 0 : res.publicKey) !== null && _b !== void 0 ? _b : provider.publicKey;
        if (!pk)
            throw new Error('Wallet did not return a public key on connect');
        return pk.toString();
    }
    async login() {
        var _a;
        setAuthLoading(true);
        try {
            const provider = this.resolveProvider();
            const address = await this.connectAddress(provider);
            // Reuse a still-valid session for THIS wallet.
            const existing = await SessionManager().getSession();
            if (existing && existing.address === address) {
                const user = { address, id: address, provider: this };
                this.authenticatedAddress = address;
                setCurrentUser(user);
                this.markAuthMethod();
                return user;
            }
            // SIWS: nonce -> canonical prompt -> the wallet signs it locally.
            // The nonce is fetched HERE, after connect, on purpose: a network
            // round trip before the wallet handoff decays the transient
            // activation the mobile wallet's intent navigation needs.
            const nonce = await genAuthNonce();
            const message = await genSolanaMessage(address, nonce);
            const messageBytes = getPlatform().textEncode(message);
            await this.freshActivation(provider, 'login', address);
            const signResult = await provider.signMessage(messageBytes, 'utf8');
            const signatureBytes = (_a = signResult === null || signResult === void 0 ? void 0 : signResult.signature) !== null && _a !== void 0 ? _a : signResult;
            // base64 detached ed25519 signature — matches guest/privy providers and
            // the bounded-auth issuer's base64ToBytes() verify path.
            const signature = Buffer.from(signatureBytes).toString('base64');
            const result = await createSessionWithSignature(address, message, signature);
            await SessionManager().storeSession(address, result.accessToken, result.idToken, result.refreshToken);
            this.markAuthMethod();
            const user = { address, id: address, provider: this };
            this.authenticatedAddress = address;
            setCurrentUser(user);
            return user;
        }
        finally {
            setAuthLoading(false);
        }
    }
    /**
     * DS2-0369: refuse to sign when the browser wallet's currently selected account
     * differs from the account this session was authenticated with. Signing under a
     * switched account would produce a message/transaction from an identity the
     * Bounded session never proved. Require a fresh wallet sign-in to rebind.
     */
    assertAuthenticatedAccount(currentAddress, action) {
        var _a;
        if (!this.authenticatedAddress || currentAddress !== this.authenticatedAddress) {
            throw new Error(`The connected Solana wallet account changed after login (session is for ` +
                `${(_a = this.authenticatedAddress) !== null && _a !== void 0 ? _a : 'no account'}, wallet is on ${currentAddress}). ` +
                `Sign in again with the wallet before ${action}.`);
        }
    }
    /**
     * Give the caller a chance to collect a fresh user gesture immediately
     * before a wallet call. Solana Mobile needs one per operation (each
     * dispatches an Android intent through window.location.assign, and the
     * originating tap is spent by the time preparation finishes). A rejection
     * aborts the operation before anything is signed. No-op when the app
     * supplied no hook, which is every injected-wallet app.
     */
    async freshActivation(provider, action, expected) {
        var _a;
        await this.requireFreshTap(provider, action);
        // DS2-0369, at the last possible moment: the gesture is an arbitrarily
        // long human pause, and the wallet can move to another account during it
        // (a mobile reauthorization does exactly that). Re-read and re-check now,
        // so nothing but the wallet call itself follows this assertion.
        if (!expected)
            return;
        const current = (_a = provider.publicKey) === null || _a === void 0 ? void 0 : _a.toString();
        if (current === expected)
            return;
        if (!current) {
            // publicKey is optional on the provider interface, so a wallet may
            // simply stop reporting one. With an account expected, "cannot tell"
            // is not "unchanged" - refuse rather than sign blind.
            throw new Error(`The Solana wallet stopped reporting a connected account before ${action}. ` +
                `Reconnect the wallet and try again.`);
        }
        if (action === 'login') {
            // Not a violation of a session - there may not be one yet - but the
            // challenge was built for the account we connected, so signing it
            // with another produces a proof the issuer will reject anyway.
            throw new Error(`The connected Solana wallet switched to ${current} while this sign-in was being ` +
                `approved (it started on ${expected}). Start the wallet sign-in again.`);
        }
        this.assertAuthenticatedAccount(current, action);
    }
    /**
     * The gesture half of freshActivation, on its own because a reconnect needs
     * it too: finish the provider's async work, then collect the tap.
     */
    async requireFreshTap(provider, action) {
        // Finish the provider's own async work FIRST: a lazy chunk fetched after
        // the tap can outlive the transient activation the wallet's intent
        // navigation needs, which is the failure this whole hook exists for.
        if (provider.prepare)
            await provider.prepare(action);
        // Only wallets that leave the page need the tap. Applying it to an
        // in-page wallet would be pure friction, and deciding here (rather than
        // in the caller) means a caller that supplies the hook is always
        // correct: it fires exactly when the resolved wallet needs it.
        if (provider.leavesPage && this.config.confirmWalletAction) {
            await this.config.confirmWalletAction(action);
        }
    }
    /**
     * DS2-0369 on the way BACK. The check before a wallet call is not enough for
     * a wallet that re-authorizes INSIDE it: Solana Mobile does exactly that on
     * both transports, and discards the account the request named, so it can
     * return a transaction signed by an account this session never proved. Read
     * the account again once the wallet answers, before that answer is used.
     *
     * LF-003: the message must MEAN the same thing, not BE the same bytes. A
     * wallet is allowed to guard the transaction it signs - Phantom injects
     * Lighthouse assertions on mainnet - and doing so recompiles the message and
     * renumbers every account index, which no byte comparison can survive. What
     * cannot change is what our own instructions do; `describeInstructionDivergence`
     * is the check for that, and it is strictly stronger than a length-and-bytes
     * diff for everything except the parts a wallet is entitled to touch.
     */
    async assertSignedByAuthenticatedAccount(signed, message, action) {
        const expected = this.authenticatedAddress;
        if (!expected)
            return;
        const after = transactionMessageBytes(signed);
        if (after.length !== message.length || after.some((b, i) => b !== message[i])) {
            // Parse the SNAPSHOT rather than the caller's object: a wallet that
            // signs in place has already mutated that object by now.
            const { VersionedMessage } = await import('@solana/web3.js');
            let divergence;
            try {
                divergence = describeInstructionDivergence(VersionedMessage.deserialize(message), VersionedMessage.deserialize(after));
            }
            catch (_a) {
                // Unreadable either side: cannot show the instructions survived.
                divergence = 'the transaction it signed could not be read';
            }
            if (divergence !== null) {
                throw new Error(`The Solana wallet returned a different transaction than the one it was asked to ` +
                    `sign, so nothing was ${action}: ${divergence}. Try again.`);
            }
        }
        if (transactionSignedBy(signed, expected))
            return;
        throw new Error(`The Solana wallet did not sign as ${expected}, which is the account this session was ` +
            `authenticated with, so nothing was ${action}. This happens when the wallet re-authorizes ` +
            `onto another account - sign in again with the wallet you want to use.`);
    }
    markAuthMethod() {
        // Mark the auth method so clearIncompatibleSession() doesn't wipe this
        // wallet session on the next init().
        setStoredAuthMethod(INJECTED_AUTH_METHOD);
    }
    async restoreSession() {
        const session = await SessionManager().getSession();
        if (!session)
            return null;
        this.authenticatedAddress = session.address;
        const user = { address: session.address, id: session.address, provider: this };
        setCurrentUser(user);
        return user;
    }
    async logout() {
        // Clear the Bounded session; best-effort disconnect the wallet.
        this.authenticatedAddress = null;
        await SessionManager().clearSession();
        try {
            // Same resolution chain as signing, so a registry-only wallet (MWA)
            // is deauthorized too; resolveProvider throwing (no wallet) is fine.
            const provider = this.resolveProvider();
            if (typeof provider.disconnect === 'function')
                await provider.disconnect();
        }
        catch ( /* ignore */_a) { /* ignore */ }
        setCurrentUser(null);
    }
    // --- Full local-keypair signing surface (the wallet's OWN methods) --------
    async signMessage(message) {
        var _a;
        const provider = this.resolveProvider();
        const address = await this.connectForAction(provider);
        this.assertAuthenticatedAccount(address, 'signing');
        const messageBytes = getPlatform().textEncode(message);
        await this.freshActivation(provider, 'signMessage', this.authenticatedAddress);
        const res = await provider.signMessage(messageBytes, 'utf8');
        const signatureBytes = (_a = res === null || res === void 0 ? void 0 : res.signature) !== null && _a !== void 0 ? _a : res;
        // base58 is the standard Solana signature encoding for app-facing signMessage.
        return bs58.encode(signatureBytes instanceof Uint8Array ? signatureBytes : new Uint8Array(signatureBytes));
    }
    async signTransaction(transaction) {
        const provider = this.resolveProvider();
        const address = await this.connectForAction(provider);
        this.assertAuthenticatedAccount(address, 'signing this transaction');
        await this.freshActivation(provider, 'signTransaction', this.authenticatedAddress);
        // What the signature must cover, captured before the wallet sees it.
        const message = transactionMessageBytes(transaction);
        const signed = await provider.signTransaction(transaction);
        await this.assertSignedByAuthenticatedAccount(signed, message, 'returned');
        return signed;
    }
    /**
     * SIMD-0385 v1. Only a Wallet Standard shim exposes `signTransactionV1`, and
     * only for a wallet that advertised version 1, so an injected legacy provider
     * never reaches here: the write request advertised legacy + v0 for it.
     */
    async signTransactionV1(transaction) {
        const provider = this.resolveProvider();
        const address = await this.connectForAction(provider);
        this.assertAuthenticatedAccount(address, 'signing this v1 transaction');
        if (!provider.signTransactionV1) {
            throw new Error('This wallet does not support Solana transaction v1 signing.');
        }
        await this.freshActivation(provider, 'signTransaction', this.authenticatedAddress);
        const message = Uint8Array.from(transaction.messageBytes);
        const signed = await provider.signTransactionV1(transaction);
        if (signed.messageBytes.length !== message.length || signed.messageBytes.some((byte, index) => byte !== message[index])) {
            throw new Error('The wallet changed the v1 transaction message instead of signing it.');
        }
        if (!transactionV1SignedBy(signed, this.authenticatedAddress)) {
            throw new Error('The wallet did not sign the v1 transaction with the account authenticated in this session.');
        }
        return signed;
    }
    /** What the underlying wallet advertised; a legacy injected provider is legacy + v0. */
    supportedTransactionVersions() {
        var _a;
        try {
            const provider = this.resolveProvider();
            const declared = (_a = provider.supportedTransactionVersions) === null || _a === void 0 ? void 0 : _a.call(provider);
            if (Array.isArray(declared) && declared.length > 0 && typeof provider.signTransactionV1 === 'function') {
                return declared.filter((v) => v === 'legacy' || v === 0 || v === 1);
            }
        }
        catch (_b) {
            // No provider resolvable yet: the safe answer is the baseline.
        }
        return ['legacy', 0];
    }
    async signAndSubmitTransaction(transaction, _feePayer) {
        const { Connection, PublicKey } = await import("@solana/web3.js");
        const provider = this.resolveProvider();
        const address = await this.connectForAction(provider);
        this.assertAuthenticatedAccount(address, 'submitting this transaction');
        const rpcUrl = this.getRpcUrl();
        const connection = new Connection(rpcUrl, 'confirmed');
        // Set a fresh blockhash on the tx (mirrors the other providers).
        // DS3-0414: a Solana signature covers the compiled message (recentBlockhash
        // included), so refreshing the blockhash after any signer (sponsor / co-signer)
        // has signed silently invalidates their signature. Only refresh while the
        // transaction is still unsigned; once a real signature is present, submit the
        // caller's transaction verbatim.
        if (!transactionHasSignature(transaction)) {
            const { blockhash } = await connection.getLatestBlockhash('confirmed');
            const isLegacy = 'recentBlockhash' in transaction &&
                !('message' in transaction && 'staticAccountKeys' in transaction.message);
            if (isLegacy) {
                const legacy = transaction;
                legacy.recentBlockhash = blockhash;
                if (!legacy.feePayer)
                    legacy.feePayer = new PublicKey(address);
            }
            else {
                transaction.message.recentBlockhash = blockhash;
            }
        }
        // DS2-0369 again, at the LAST moment: the blockhash fetch above is an
        // await, and the wallet's selected account can move during it (an MWA
        // reauthorization does exactly that). Re-read it here so a switch that
        // happened while we prepared cannot be signed under the old session,
        // then take the gesture so nothing else can intervene before the call.
        const signingAddress = await this.connectForAction(provider);
        this.assertAuthenticatedAccount(signingAddress, 'submitting this transaction');
        await this.freshActivation(provider, 'signAndSubmitTransaction', this.authenticatedAddress);
        // SIGN, VERIFY, THEN SUBMIT whenever the wallet can sign - which is every
        // wallet that is not sign-and-send-only. A native send hands the
        // transaction straight to the network, so there is no moment at which
        // anyone can check that it was signed by the account this session was
        // authenticated with. Signing gives us that moment, and submitting
        // ourselves costs only the RPC round trip we already make to confirm.
        //
        // Deliberately NOT decided by which wallet this is: Solana Mobile is the
        // one known to re-authorize mid-call, but a display name is not a
        // security boundary and the verification is worth having for every
        // wallet. Ask the LIVE capability when the provider offers one - a
        // Wallet Standard wallet can rewrite its feature set during connect, so
        // a method being present is not proof it works.
        const canSign = provider.canSignTransaction
            ? provider.canSignTransaction()
            : typeof provider.signTransaction === 'function';
        if (canSign) {
            const message = transactionMessageBytes(transaction);
            const signed = await provider.signTransaction(transaction);
            // The last moment at which refusing still costs nothing: after this
            // the transaction is on the network.
            await this.assertSignedByAuthenticatedAccount(signed, message, 'submitted');
            const raw = signed.serialize();
            const signature = await connection.sendRawTransaction(raw);
            await confirmAndCheckTransaction(connection, signature);
            return signature;
        }
        // Neither capability: this wallet cannot put a transaction on the chain
        // at all, and pointing at a native send it does not have would only
        // waste the reader's time.
        const canNativeSend = provider.canSignAndSend
            ? provider.canSignAndSend()
            : typeof provider.signAndSendTransaction === 'function';
        if (!canNativeSend) {
            throw new Error('This wallet can neither sign nor send transactions right now. A Wallet Standard ' +
                'wallet reports its capabilities per account, so check that the connected account ' +
                'supports solana:signTransaction or solana:signAndSendTransaction.');
        }
        // Sign-and-send-only: the wallet broadcasts, so there is no moment at
        // which anything can be checked - and "the network would reject a
        // wrong-account signature anyway" is not true in general, because a
        // transaction whose required signer is NOT the session account is
        // perfectly valid on-chain. This method promises that what it submits
        // was signed by the account the session was authenticated with, and here
        // it cannot keep that promise, so it does not make it. An app that wants
        // the wallet's own broadcast can take the wallet itself through
        // getNativeMethods() - outside this guarantee, and knowingly.
        throw new Error('This wallet can only sign-and-send, which broadcasts before Bounded can check that the ' +
            'session account signed. Drive that wallet directly if you accept that - ' +
            'const wallet = await (await getAuthProvider()).getNativeMethods() - ' +
            'or use a wallet that can sign transactions.');
    }
    async getNativeMethods() {
        return this.resolveProvider();
    }
    getRpcUrl(network) {
        return resolveSolanaRpcUrl(this.networkUrl, network !== null && network !== void 0 ? network : this.configuredNetwork, 'Wallet-login Solana transaction');
    }
}
